/**
 * AdSenseSlot — Real Google AdSense ad slot, env-gated.
 *
 * Activation: set REACT_APP_ADSENSE_CLIENT_ID + REACT_APP_ADSENSE_SLOT_INTERSTITIAL
 * in /app/frontend/.env. Until then, returns null and the simulated <AdPopup>
 * creative is shown instead.
 *
 * Why this design:
 *  - Zero impact on local dev (no AdSense script loaded if env is empty).
 *  - Zero impact on premium users (caller already gates by user.premium).
 *  - Lazy-loads the AdSense JS exactly once per session.
 *  - Honors Google's "responsive auto" ad unit format inside a 300x280 box,
 *    which is the most reliable interstitial-style slot for SPAs.
 *
 * Real-money / sweepstakes pages MUST NOT render this slot.
 */
import { useEffect, useRef } from 'react';

const ADSENSE_SRC = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';
let scriptLoadedPromise = null;

function loadAdSenseScript(clientId) {
  if (scriptLoadedPromise) return scriptLoadedPromise;
  scriptLoadedPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject('no-window');
    if (document.querySelector('script[data-emergent-adsense]')) return resolve();
    const s = document.createElement('script');
    s.async = true;
    s.crossOrigin = 'anonymous';
    s.dataset.emergentAdsense = '1';
    s.src = `${ADSENSE_SRC}?client=${encodeURIComponent(clientId)}`;
    s.onload = resolve;
    s.onerror = () => { scriptLoadedPromise = null; reject('script-load-failed'); };
    document.head.appendChild(s);
  });
  return scriptLoadedPromise;
}

export default function AdSenseSlot() {
  const clientId = process.env.REACT_APP_ADSENSE_CLIENT_ID;
  const slotId = process.env.REACT_APP_ADSENSE_SLOT_INTERSTITIAL;
  const insRef = useRef(null);

  const configured = Boolean(clientId && clientId.startsWith('ca-pub-') && slotId);

  useEffect(() => {
    if (!configured) return;
    let cancelled = false;
    loadAdSenseScript(clientId).then(() => {
      if (cancelled) return;
      try {
        // adsbygoogle queue API — fires the ad request for this slot.
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch (e) { console.warn('[adsense] push failed:', e); }
    }).catch((e) => console.warn('[adsense] script failed:', e));
    return () => { cancelled = true; };
  }, [configured, clientId]);

  if (!configured) return null;

  return (
    <ins
      ref={insRef}
      className="adsbygoogle"
      style={{ display: 'block', width: '100%', minHeight: 250 }}
      data-ad-client={clientId}
      data-ad-slot={slotId}
      data-ad-format="auto"
      data-full-width-responsive="true"
      data-testid="adsense-slot-interstitial"
    />
  );
}

/** True if AdSense env vars are present (used by AdPopup to pick which body to render). */
export function isAdSenseConfigured() {
  const c = process.env.REACT_APP_ADSENSE_CLIENT_ID;
  const s = process.env.REACT_APP_ADSENSE_SLOT_INTERSTITIAL;
  return Boolean(c && c.startsWith('ca-pub-') && s);
}
